import Vue from 'vue';
import { mapGetters } from 'vuex';
import path from 'path';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';

export default Vue.component('background-images', {
  template: require('./template.html'),
  name: 'background-images',
  mixins: [guid, uploader],
  components: {
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport', 'activeDroplet']),
    allColumns() {
      let list = [];
      const droplet = this.activeDroplet;
      if (droplet.rows) {
        _.forEach(this.activeDroplet.rows,
          row => _.forEach(row.columns, column => list.push(column)));
      }
      if (droplet.cards) {
        list = list.concat(droplet.cards);
      }
      if (droplet.columns) {
        list = list.concat(droplet.columns);
      }
      return { list };
    },
    setClass() {
      let cls = '';
      if (this.activeDroplet.type === 'puddle.columns') {
        const cols = this.activeDroplet.columns.length;
        cls = `col-${12 / cols}`;
        cls = cls.replace(/\./g, '-');
      }
      if ([
        'droplet.cardsEvent',
        'droplet.cardsWide',
        'droplet.cardsGeneral',
      ].includes(this.activeDroplet.type)) {
        cls = 'drz-col-6';
      }
      if (['droplet.cardsTall'].includes(this.activeDroplet.type)) {
        cls = 'drz-col-4';
      }
      return cls;
    },
    nonGrid() {
      return this.activeDroplet.type === 'puddle.columns' || this.activeDroplet.type.match(/^droplet\.cards/gi);
    },
    hasBgImg() {
      const activeColumn = this.activeColumn;
      const selector = activeColumn._id ? _.find(activeColumn.mediaQueries, mq => mq.selector === ' ') : false;
      const property = selector ? _.find(selector.properties, mq => mq.property === 'background-image') : false;
      const hasImg = property && property[this.viewport] !== '' && property[this.viewport] !== 'unset' ? property[this.viewport] : false;
      return hasImg;
    },
  },
  props: ['option', 'active-state', 'dom-nodes'],
  data() {
    return {
      display: {},
      debTime: 250,
      innerDomNodes: {},
      activeImg: null,
      hoverDel: false,
      activeIndex: null,
      activeImgName: '',
      activeColumn: {},
      loading: false,
      imageFiles: [],
      showConfirm: false,
      showFiles: false,
      prefix: '',
      bucketPath: '',
      thumbnails: '',
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
  },
  created() {
    // setup defaults from payload models
    _.forOwn(this.option.payload.models, (value, key) => {
      this[key] = value;
    });
    // then check for previous values
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      if (this.activeColumn) {
        this.activeImgName = this.getBgImg(this.activeColumn);
      }
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    removeBgImg() {
      const column = this.activeColumn;
      this.activeImgName = '';
      const payload = {
        drp: column,
        val: 'unset',
        selector: ' ',
        property: 'background-image',
        viewport: this.viewport,
      };
      this.$store.dispatch('changeDropletCss', payload);
      this.$emit('check-legacy-styles', payload);
      this.$emit('css-generator', this.cssPayload(), this.option.plugin);
      this.$emit('update-changes', true);
    },
    cssPayload() {
      let payload = false;
      if (this.option.payload.useColumns) {
        payload = {
          key: 'drps',
          value: [...this.allColumns.list, this.activeDroplet],
        };
      }
      return payload;
    },
    toggleBrowse() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchImageFiles();
      }
    },
    async uploadBg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          let newFile = [...e.target.files];
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `${this.prefix}${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');

            try {
              // send off to our s3 upload mixin
              const filePath = `/${this.bucketPath}${fileName}`;
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: newFile,
                thumbnail: {
                  width: 35,
                  srcFile: newFile,
                  name: fileName,
                  bucketPath: `${this._sitePath}/${this.thumbnails}`,
                },
              });
              await this.setColumnImg(filePath);
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadBg',
                });
              }
            } finally {
              if (this.showFiles) {
                setTimeout(() => {
                  this.fetchImageFiles();
                }, 500);
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async fetchImageFiles() {
      try {
        this.loading = true;
        const assetsFolder = '/_droplet-assets';
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.thumbnails}`);
        this.imageFiles = files.map(f => `${assetsFolder}${f.split(assetsFolder)[1]}`);
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'fetchImageFiles',
        });
      } finally {
        this.loading = false;
      }
    },
    async setColumnImg(f, def) {
      // if came from click, gather full url
      let file;
      if (def) {
        file = f;
      } else {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      }
      const column = this.activeColumn;
      // set media query
      let selector = ' ';
      if (this.option.payload.selector) {
        selector = this.option.payload.selector.replace(/\$\{itemId\}/gi, column._id);
      }
      const payload = {
        drp: column,
        val: f ? `url('${file}')` : 'unset',
        selector,
        property: 'background-image',
        viewport: this.viewport,
      };
      this.$store.dispatch('changeDropletCss', payload);
      this.activeImgName = this.getBgImg();
      this.$emit('check-legacy-styles', payload);
      this.$emit('css-generator', this.cssPayload(), this.option.plugin);
      this.$emit('update-changes', true);
    },
    getBgImg() {
      const val = this._getBgImg(
        { viewport: this.viewport },
        ' ',
        'background-image',
        this.activeColumn,
      );
      return path.basename(val);
    },
    async deleteImg() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this._getFromThumb(this.activeImg));
      const thumb = path.basename(this.activeImg);
      try {
        this.loading = true;
        // delete original from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        });
        // delete thumbnail from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted image: ${file}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteImg',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.loading = false;
        this.showConfirm = false;
        this.fetchImageFiles();
      }
    },
    toggleConfirm() {
      this.showConfirm = !this.showConfirm;
    },
    confirmDelete(file) {
      this.activeImg = file;
      this.toggleConfirm();
    },
    setActiveColumn(column) {
      this.activeColumn = column;
      this.activeImgName = this.getBgImg();
    },
    checkColumnType(column) {
      return !column.covered ||
      (!column.removed && column.covered && !column.covered[this.viewport]);
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    matchBg(image) {
      const column = this.activeColumn;
      const value = this._getBgImg(
        { viewport: this.viewport },
        ' ',
        'background-image',
        column,
      );
      const realFile = `/${this.bucketPath}${this._getFromThumb(path.basename(image))}`;
      return realFile === value;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/background-images/index.js